import { useEffect, useRef } from 'react';

/**
 * AdSlot — renders a Google AdSense ad unit if AdSense is enabled.
 *
 * Off by default: nothing renders until:
 *   - REACT_APP_ADSENSE_ENABLED === 'true'
 *   - AND a slot id is provided (either via the `slot` prop or via env)
 *
 * Once approved by AdSense, set these in /app/frontend/.env:
 *   REACT_APP_ADSENSE_ENABLED=true
 *   REACT_APP_ADSENSE_SLOT_HOMEPAGE=1234567890
 *   REACT_APP_ADSENSE_SLOT_TOOL=2345678901
 *   REACT_APP_ADSENSE_SLOT_BLOG_INLINE=3456789012
 *
 * Then rebuild the frontend with `yarn build`.
 */
const CLIENT_ID = 'ca-pub-2898932919328458';

const SLOT_MAP = {
    homepage: process.env.REACT_APP_ADSENSE_SLOT_HOMEPAGE,
    tool: process.env.REACT_APP_ADSENSE_SLOT_TOOL,
    'blog-inline': process.env.REACT_APP_ADSENSE_SLOT_BLOG_INLINE,
};

function isEnabled() {
    return (
        process.env.REACT_APP_ADSENSE_ENABLED === 'true' &&
        typeof window !== 'undefined'
    );
}

export function AdSlot({
    placement,
    slot,
    format = 'auto',
    fullWidthResponsive = true,
    className = '',
    label = 'Advertisement',
}) {
    const insRef = useRef(null);
    const slotId = slot || (placement ? SLOT_MAP[placement] : undefined);

    useEffect(() => {
        if (!isEnabled() || !slotId || !insRef.current) return;
        try {
            // Push a new ad request. Safe to call once per <ins>.
            // eslint-disable-next-line no-multi-assign
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
            // Ad blockers, offline mode, etc. — silently ignore.
        }
    }, [slotId]);

    if (!isEnabled() || !slotId) return null;

    return (
        <div className={`w-full my-8 ${className}`} aria-label={label}>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 text-center mb-1">
                {label}
            </p>
            <ins
                ref={insRef}
                className="adsbygoogle block"
                style={{ display: 'block', minHeight: '90px' }}
                data-ad-client={CLIENT_ID}
                data-ad-slot={slotId}
                data-ad-format={format}
                data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
            />
        </div>
    );
}
